import { useState, useEffect, useCallback } from 'react'
import { UserCheck, Package, Tag, ChevronDown, GripVertical, Info, Search, Loader2 } from 'lucide-react'
import RRToggle from '@/components/rr/RRToggle'
import { rrGetProductTree, rrGetProductSequences, rrReorderSequence, rrToggleAgentActive } from '@/actions/rr'

const AV_COLORS = ['#2f9e6b', '#3b82c4', '#b9772e', '#8a5cd1', '#c2557a', '#2aa1a8']
function initials(n: string) { const p = n.trim().split(' '); return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() }
function avColor(name: string) { let h = 0; for (const c of name) h = (h * 31 + c.charCodeAt(0)) % AV_COLORS.length; return AV_COLORS[h] }

interface TreeProduct { product_id: number; name: string; status: string; rr_enabled: boolean; agent_count: number }
interface TreeCat { cat_id: number; cat: string; colour: string; products: TreeProduct[] }
interface SeqAgent { seq_id: number; sequence_order: number; is_active: boolean; user_id: number; name: string; email: string; is_next: boolean; today_count: number; last_at: string }

export default function MappingPage() {
  const [tree, setTree] = useState<TreeCat[]>([])
  const [sequences, setSequences] = useState<SeqAgent[]>([])
  const [activeProductId, setActiveProductId] = useState<number | null>(null)
  const [expandedCats, setExpandedCats] = useState<Set<number>>(new Set())
  const [agentSearch, setAgentSearch] = useState('')
  const [productSearch, setProductSearch] = useState('')
  const [loadingTree, setLoadingTree] = useState(true)
  const [loadingSeq, setLoadingSeq] = useState(false)
  const [reordering, setReordering] = useState(false)
  const [dragIdx, setDragIdx] = useState<number | null>(null)
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null)

  useEffect(() => {
    setLoadingTree(true)
    rrGetProductTree().then(res => {
      const cats: TreeCat[] = (res?.data ?? []) as TreeCat[]
      setTree(cats)
      if (cats.length) {
        setExpandedCats(new Set(cats.map(c => c.cat_id)))
        const firstProduct = cats[0]?.products[0]
        if (firstProduct) setActiveProductId(firstProduct.product_id)
      }
    }).finally(() => setLoadingTree(false))
  }, [])

  const loadSequences = useCallback(() => {
    if (!activeProductId) return
    setLoadingSeq(true)
    rrGetProductSequences(activeProductId)
      .then(res => setSequences((res?.data ?? []) as SeqAgent[]))
      .finally(() => setLoadingSeq(false))
  }, [activeProductId])

  useEffect(() => { loadSequences() }, [loadSequences])

  async function toggleActive(userId: number) {
    const seq = sequences.find(s => s.user_id === userId)
    if (!activeProductId || !seq) return
    setSequences(prev => prev.map(s => s.user_id === userId ? { ...s, is_active: !s.is_active } : s))
    await rrToggleAgentActive(activeProductId, userId)
  }

  function toggleCat(catId: number) {
    setExpandedCats(p => { const n = new Set(p); n.has(catId) ? n.delete(catId) : n.add(catId); return n })
  }

  async function handleDrop(targetIdx: number) {
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); setDragOverIdx(null); return }
    const reordered = [...sequences]
    const [moved] = reordered.splice(dragIdx, 1)
    reordered.splice(targetIdx, 0, moved)
    setSequences(reordered)
    setDragIdx(null)
    setDragOverIdx(null)
    setReordering(true)
    try { await rrReorderSequence(activeProductId!, reordered.map(s => s.user_id)) }
    finally { setReordering(false) }
  }

  const activeProduct = tree.flatMap(c => c.products).find(p => p.product_id === activeProductId)
  const activeCat = tree.find(c => c.products.some(p => p.product_id === activeProductId))
  const nextPos = sequences.find(s => s.is_next)?.sequence_order ?? 1
  const activeCount = sequences.filter(s => s.is_active).length
  const canDrag = !agentSearch
  const filteredSeq = sequences
    .filter(a => !agentSearch || a.name.toLowerCase().includes(agentSearch.toLowerCase()) || a.email.toLowerCase().includes(agentSearch.toLowerCase()))
    .sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1))

  return (
    <div className="h-full flex flex-col">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-brandColor flex items-center justify-center flex-shrink-0">
            <UserCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900 tracking-tight">Agent-Product Mapping</h1>
            <p className="text-sm text-gray-500 mt-0.5">Choose a product, then set which agents receive its leads and in what order.</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="grid grid-cols-[280px_1fr] gap-4 items-start">
          {/* Left — product tree */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Products</span>
            </div>
            <div className="px-3 pt-2 pb-1">
              <div className="flex items-center gap-2 h-8 border border-gray-300 rounded-lg px-2.5 text-gray-400">
                <Search className="w-3.5 h-3.5 flex-shrink-0" />
                <input value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Search products…" className="flex-1 text-xs text-gray-900 border-0 outline-none bg-transparent" />
              </div>
            </div>
            <div className="p-2 max-h-[60vh] overflow-y-auto">
              {loadingTree
                ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                : tree.map(({ cat_id, cat, colour, products }) => {
                  const ps = productSearch
                    ? products.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()))
                    : products
                  if (!ps.length) return null
                  const expanded = productSearch ? true : expandedCats.has(cat_id)
                  return (
                    <div key={cat_id}>
                      <button onClick={() => toggleCat(cat_id)} className="w-full flex items-center gap-2 px-2 py-2 text-left font-bold text-xs text-gray-800">
                        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expanded ? '' : '-rotate-90'}`} />
                        <Tag className="w-3.5 h-3.5" style={{ color: colour }} />
                        {cat}
                      </button>
                      {expanded && ps.map(p => (
                        <button key={p.product_id} onClick={() => setActiveProductId(p.product_id)}
                          className={`w-full flex items-center gap-2 pl-7 pr-2 py-2 rounded-lg text-left text-sm transition-colors ${p.product_id === activeProductId ? 'bg-green-50 text-brandColor font-bold' : 'text-gray-600 hover:bg-gray-50'}`}>
                          <Package className={`w-3.5 h-3.5 flex-shrink-0 ${p.product_id === activeProductId ? 'text-brandColor' : 'text-gray-400'}`} />
                          <span className="flex-1 truncate">{p.name}</span>
                          {p.agent_count > 0 && <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{p.agent_count}</span>}
                        </button>
                      ))}
                    </div>
                  )
                })
              }
            </div>
          </div>

          {/* Right — agent sequence */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-50 text-brandColor flex items-center justify-center">
                  <Package className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-extrabold text-gray-900">{activeProduct?.name ?? 'Select a product'}</span>
                    {activeCat && <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{activeCat.cat}</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {sequences.length} agents in sequence
                    {activeCount < sequences.length && <span className="ml-1 text-amber-500 font-semibold">· {activeCount} active</span>}
                    {activeCount === sequences.length && <span className="ml-1">· {activeCount} active</span>}
                    {' '}· pointer at position {nextPos}
                    {reordering && <span className="ml-2 text-brandColor"><Loader2 className="w-3 h-3 inline animate-spin" /> saving…</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 h-9 border border-gray-300 rounded-lg px-3 text-gray-400 w-56">
                <Search className="w-4 h-4 flex-shrink-0" />
                <input value={agentSearch} onChange={e => setAgentSearch(e.target.value)} placeholder="Search agents…" className="flex-1 text-sm text-gray-900 border-0 outline-none bg-transparent" />
              </div>
            </div>

            <div className="grid grid-cols-[60px_1fr_120px] px-5 py-2.5 text-[10px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100">
              <span>Sequence</span><span>Agent</span><span className="text-center">Active in RR</span>
            </div>

            <div className="divide-y divide-gray-50 px-3 py-2">
              {loadingSeq
                ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>
                : filteredSeq.map((agent, i) => {
                  const seqIdx = sequences.findIndex(s => s.seq_id === agent.seq_id)
                  const isDragging = dragIdx === seqIdx
                  const isOver = dragOverIdx === seqIdx
                  return (
                    <div
                      key={agent.seq_id}
                      draggable={canDrag}
                      onDragStart={() => canDrag && setDragIdx(seqIdx)}
                      onDragOver={e => { if (canDrag) { e.preventDefault(); setDragOverIdx(seqIdx) } }}
                      onDragEnd={() => { setDragIdx(null); setDragOverIdx(null) }}
                      onDrop={() => canDrag && handleDrop(seqIdx)}
                      className={`grid grid-cols-[24px_30px_1fr_120px] items-center gap-3 px-2 py-3 rounded-xl transition-colors
                        ${agent.is_next ? 'bg-green-50 ring-1 ring-inset ring-green-200' : ''}
                        ${!agent.is_active ? 'opacity-50' : ''}
                        ${isDragging ? 'opacity-30' : ''}
                        ${isOver && !isDragging ? 'border-t-2 border-brandColor' : ''}
                        ${canDrag ? 'cursor-grab' : ''}
                      `}
                    >
                      <GripVertical className={`w-4 h-4 ${canDrag ? 'text-gray-300 cursor-grab' : 'text-gray-200'}`} />
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-extrabold ${agent.is_next ? 'bg-brandColor text-white' : 'bg-gray-100 text-gray-500'}`}>{i + 1}</div>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0" style={{ background: avColor(agent.name) }}>{initials(agent.name)}</span>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                            {agent.name}
                            {agent.is_next && <span className="text-[9px] font-extrabold text-white bg-brandColor px-1.5 py-0.5 rounded-full uppercase tracking-wider">Next</span>}
                          </div>
                          <div className="text-xs text-gray-400">{agent.email} · {agent.today_count} today{agent.last_at !== '—' ? ` · last ${agent.last_at}` : ''}</div>
                        </div>
                      </div>
                      <div className="flex justify-center">
                        <RRToggle on={agent.is_active} onChange={() => toggleActive(agent.user_id)} />
                      </div>
                    </div>
                  )
                })
              }
              {!loadingSeq && filteredSeq.length === 0 && agentSearch && (
                <div className="py-8 text-center text-sm text-gray-400">No agents match "{agentSearch}"</div>
              )}
            </div>

            <div className="flex items-start gap-2 px-5 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-300" />
              {canDrag
                ? <>The highlighted agent is <b className="text-gray-600">next</b> to receive a lead. Drag rows to reorder. Toggle off to pause.</>
                : <>Clear search to drag and reorder agents. Toggle off to pause without changing order.</>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
